/**
 * Reasoning effort levels the AI SDK's `reasoning` call setting accepts.
 * Only reasoning models (gpt-5 and later) use it; others ignore it.
 */
export const REASONING_EFFORTS = [
  "none",
  "minimal",
  "low",
  "medium",
  "high",
  "xhigh",
] as const;

export type ReasoningEffort = (typeof REASONING_EFFORTS)[number];

export interface ModelSetting {
  model: string;
  /** Unset leaves the model on its own default effort. */
  reasoningEffort?: ReasoningEffort;
}

/**
 * Read a reasoning effort env var. A typo is ignored with a warning rather
 * than passed on, since the SDK would reject it and fail every AI call on
 * that model. Takes the value directly so each env var stays a static
 * `process.env.X` reference.
 */
function parseReasoningEffort(
  value: string | undefined,
  envName: string
): ReasoningEffort | undefined {
  const effort = value?.trim().toLowerCase();
  if (!effort) {
    return undefined;
  }
  if ((REASONING_EFFORTS as readonly string[]).includes(effort)) {
    return effort as ReasoningEffort;
  }
  console.warn(
    `[config] Ignoring ${envName}="${value}": expected one of ${REASONING_EFFORTS.join(", ")}`
  );
  return undefined;
}

const config = {
  maxFileUpload: 3,
  maxFileSize: 6000001, // 6MB
  maxFloatRecipients: 30,
  maxFloatCandidates: 100,
  maxFloatSlugLength: 45,

  maxSubmissionRecipients: 30,
  maxSubmissionCandidates: 100,
  maxSubmissionSlugLength: 45,

  drawerWidth: 240,

  candidateAttributes: ["Languages", "Tools", "Skills", "Education"],

  floatDefaults: {
    initialSkills: 6,
    initialRoles: 3,
    initialProjects: 2,
    initialEducation: 3,
  },

  validContactPlaceholders: ["first_name", "last_name", "company"],

  csvboxCandidateSheetLicenceKey:
    process.env.NEXT_PUBLIC_CSVBOX_CANDIDATE_SHEET_KEY,
  csvboxContactSheetLicenceKey:
    process.env.NEXT_PUBLIC_CSVBOX_CONTACT_SHEET_KEY,

  pusherAppKey: process.env.NEXT_PUBLIC_PUSHER_APP_KEY,
  apiBaseUrl: process.env.NEXT_PUBLIC_LARAVEL_API_URL,

  // Upper bound for requests on the public Float/Submission share pages so a
  // stalled/blocked request fails fast instead of hanging the page indefinitely.
  // Applied per-request (not globally) so long-running authenticated calls — AI
  // chat, CV import, text enhancement — are unaffected. Override via
  // NEXT_PUBLIC_PUBLIC_PAGE_API_TIMEOUT (ms).
  publicPageApiTimeout:
    Number(process.env.NEXT_PUBLIC_PUBLIC_PAGE_API_TIMEOUT) || 30000,

  privacyPolicyUrl: "https://floats.ai/privacy.html",
  termsOfServiceUrl: "https://floats.ai/terms.html",

  confettiColours: ["#13319a", "#e50b71", "#4c91f7", "#F9E8FF"],

  defaultSkillLogo: "/img/floats/skill-icon.png",

  // The app's two model options. Recruiters choose between them for CV
  // import ("default" is standard, "fast" is light); the light model also
  // runs SOLA and every other in-app AI feature. Resolve them through
  // aiModel() in src/lib/ai/models.ts rather than reading them here.
  ai: {
    standard: {
      model: process.env.AI_MODEL || "gpt-5-mini",
      reasoningEffort: parseReasoningEffort(
        process.env.AI_MODEL_REASONING_EFFORT,
        "AI_MODEL_REASONING_EFFORT"
      ),
    } as ModelSetting,
    light: {
      model: process.env.AI_MODEL_LIGHT || "gpt-4.1-mini",
      reasoningEffort: parseReasoningEffort(
        process.env.AI_MODEL_LIGHT_REASONING_EFFORT,
        "AI_MODEL_LIGHT_REASONING_EFFORT"
      ),
    } as ModelSetting,
  },

  cvImport: {
    promptName: process.env.CV_IMPORT_PROMPT_NAME || "import_master",
    // The verbatim raw_resume_text stage can run on its own model. Unset, it
    // uses whichever model option the recruiter chose, like the other stages.
    rawText: (process.env.CV_IMPORT_RAW_TEXT_MODEL
      ? {
          model: process.env.CV_IMPORT_RAW_TEXT_MODEL,
          reasoningEffort: parseReasoningEffort(
            process.env.CV_IMPORT_RAW_TEXT_REASONING_EFFORT,
            "CV_IMPORT_RAW_TEXT_REASONING_EFFORT"
          ),
        }
      : null) as ModelSetting | null,
    // The "Faster" CV import setting can run on its own model, separate from
    // the light option that SOLA and the other in-app AI features share.
    // Unset, it uses the light option.
    fast: (process.env.CV_IMPORT_FAST_MODEL
      ? {
          model: process.env.CV_IMPORT_FAST_MODEL,
          reasoningEffort: parseReasoningEffort(
            process.env.CV_IMPORT_FAST_REASONING_EFFORT,
            "CV_IMPORT_FAST_REASONING_EFFORT"
          ),
        }
      : null) as ModelSetting | null,
  },

  jobBrief: {
    promptName: process.env.JOB_BRIEF_PROMPT_NAME || "import_job_brief",
  },
};

export default config;
